import { t } from 'i18next';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { FormProvider, useFormContext } from 'core/states';
import { getEmptyErrorMessage } from 'core/yup';

import { getGroupKey } from './groups';

const GROUP_NAME_MAX_LENGTH = 255;

interface IGroupNameForm {
  name: string;
}

// A group's name is its own in the family, compared as the groups compare it.
const groupNameSchema = (takenNames: string[]) =>
  yup.object().shape({
    name: yup
      .string()
      .trim()
      .required(getEmptyErrorMessage(t('labelGroupName')))
      .max(
        GROUP_NAME_MAX_LENGTH,
        t('errorFieldMaxLength', { field: t('labelGroupName'), length: GROUP_NAME_MAX_LENGTH })
      )
      .test(
        'group-name-unique',
        () => t('errorPartFinderFamilyGroupNameTaken'),
        (name) => !takenNames.some((taken) => getGroupKey(taken) === getGroupKey(name ?? ''))
      ),
  });

interface IGroupNameProps {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string) => void;
}

const GroupNameFields = ({ title, isOpen, onClose, onSave }: IGroupNameProps) => {
  const { t } = useTranslation();

  const { control, errors } = useFormContext();

  const handleOnClose = (reset: UseFormReset<IGroupNameForm>) => {
    onClose();
    reset();
  };

  const onSubmit = (reset: UseFormReset<IGroupNameForm>) => (data: IGroupNameForm) => {
    onSave(data.name.trim());
    handleOnClose(reset);
  };

  return (
    <Form
      title={title}
      isOpen={isOpen}
      onClose={handleOnClose}
      onSubmit={onSubmit}
      submitBtnLabel={t('buttonLabelSaveChanges')}
      cancelBtnLabel={t('buttonLabelDiscardChanges')}
      level={1}
    >
      <FormField
        name="name"
        type={InputType.TEXT_INPUT}
        inputProps={{ control, error: errors?.name, label: t('labelGroupName'), required: true }}
      />
    </Form>
  );
};

interface IGroupPanelProps extends IGroupNameProps {
  name?: string;
  takenNames: string[];
}

// Names a new group, or renames one (Edit Group).
export const GroupNamePanel = ({ name = '', takenNames, ...props }: IGroupPanelProps) => (
  <FormProvider schema={groupNameSchema(takenNames)} defaultValues={{ name }} resetDefaultValues>
    <GroupNameFields {...props} />
  </FormProvider>
);

interface IMoveToGroupForm {
  group: string | null;
}

interface IMoveToGroupProps {
  groupNames: string[];
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string) => void;
}

const MoveToGroupFields = ({ groupNames, isOpen, onClose, onSave }: IMoveToGroupProps) => {
  const { t } = useTranslation();

  const { control, errors } = useFormContext();

  const handleOnClose = (reset: UseFormReset<IMoveToGroupForm>) => {
    onClose();
    reset();
  };

  const onSubmit = (reset: UseFormReset<IMoveToGroupForm>) => (data: IMoveToGroupForm) => {
    onSave(data.group);
    handleOnClose(reset);
  };

  return (
    <Form
      title={t('titleMoveToGroup')}
      isOpen={isOpen}
      onClose={handleOnClose}
      onSubmit={onSubmit}
      submitBtnLabel={t('buttonLabelSaveChanges')}
      cancelBtnLabel={t('buttonLabelDiscardChanges')}
      level={1}
    >
      <FormField
        name="group"
        type={InputType.SELECT}
        inputProps={{
          control,
          error: errors?.group,
          label: t('labelSelectAGroup'),
          required: true,
          options: groupNames,
          getValue: (name: string) => name,
          getLabel: (name: string) => name,
        }}
      />
    </Form>
  );
};

// Move to group: one of the family's groups for an attribute on its own.
export const MoveToGroupPanel = (props: IMoveToGroupProps) => (
  <FormProvider
    schema={yup.object().shape({
      group: yup
        .string()
        .nullable()
        .required(getEmptyErrorMessage(t('labelGroup'))),
    })}
    defaultValues={{ group: null }}
    resetDefaultValues
  >
    <MoveToGroupFields {...props} />
  </FormProvider>
);
